import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { doc, setDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { AdminAuthProvider, useAdminAuth, signIn, signOut, resetPassword, friendlyAuthError } from '../cms/adminAuth';
import { videoSlots, imageSlots, videoGroups, imageGroups, toEmbedUrl } from '../cms/slots';
import { useAllPosts, slugify } from '../cms/blog';
import { Field, Pill, Toast, UploadButton, inputClass } from '../cms/AdminUI';

const CONTENT_DOC = ['site', 'content'];

/* ---------------------------------------------------------------- sign in */

function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    setError('');
    setNote('');
    setBusy(true);
    try {
      await signIn(email.trim(), password);
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  async function forgot() {
    if (!email.trim()) {
      setError('Enter your email address first, then choose reset.');
      return;
    }
    try {
      await resetPassword(email.trim());
      setNote('If that address has an account, a reset link is on its way.');
    } catch {
      setNote('If that address has an account, a reset link is on its way.');
    }
  }

  return (
    <div className="mx-auto max-w-sm px-6 py-24">
      <img src="/logo/edai-mark-dark-transparent.svg" alt="" aria-hidden="true" className="h-7 w-auto" />
      <h1 className="mt-6 font-serif text-3xl text-bright">Content console</h1>
      <p className="mt-2 text-sm leading-relaxed text-slate">
        Sign in to change images, videos, and writing on edaisystems.com.
      </p>
      <div className="mt-8 space-y-4">
        <Field label="Email" htmlFor="admin-email">
          <input
            id="admin-email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field label="Password" htmlFor="admin-password">
          <input
            id="admin-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            className={inputClass}
          />
        </Field>
        {error && <p className="text-sm text-rose-300">{error}</p>}
        {note && <p className="text-sm text-seal">{note}</p>}
        <button type="button" onClick={submit} disabled={busy} className="btn-primary w-full disabled:opacity-60">
          {busy ? 'Signing in' : 'Sign in'}
        </button>
        <button
          type="button"
          onClick={forgot}
          className="text-xs text-slate underline decoration-hairline underline-offset-4 hover:text-bright"
        >
          Forgot your password?
        </button>
      </div>
      <p className="mt-10 text-xs text-faint">
        <Link to="/" className="underline decoration-hairline underline-offset-4 hover:text-slate">
          Back to the site
        </Link>
      </p>
    </div>
  );
}

/* ------------------------------------------------------------ video editor */

function VideoEditor({ content, save, toast }) {
  const [drafts, setDrafts] = useState({});

  const value = (key, field) => {
    const draft = drafts[key];
    if (draft && field in draft) return draft[field];
    return (content.videos && content.videos[key] && content.videos[key][field]) || '';
  };

  const setDraft = (key, field, v) =>
    setDrafts((d) => ({ ...d, [key]: { ...(d[key] || {}), [field]: v } }));

  async function saveSlot(key) {
    const pasted = value(key, 'url');
    const uploaded = value(key, 'src');
    const caption = value(key, 'caption');
    const poster = value(key, 'poster');

    let embed = '';
    if (pasted) {
      embed = toEmbedUrl(pasted);
      if (!embed) {
        toast('That does not look like a YouTube or Vimeo link.', 'bad');
        return;
      }
    }
    if (!embed && !uploaded) {
      toast('Paste a video link or upload a file first.', 'bad');
      return;
    }
    await save({
      videos: {
        ...content.videos,
        [key]: { embed: embed || '', src: embed ? '' : uploaded, caption, poster, updatedAt: Date.now() },
      },
    });
    setDrafts((d) => ({ ...d, [key]: {} }));
    toast('Saved. The site updates immediately.');
  }

  async function clearSlot(key) {
    const next = { ...content.videos };
    delete next[key];
    await save({ videos: next });
    setDrafts((d) => ({ ...d, [key]: {} }));
    toast('Cleared. The page shows its placeholder again.');
  }

  return (
    <div className="space-y-12">
      <p className="max-w-measure-wide text-sm leading-relaxed text-slate">
        Paste a YouTube or Vimeo link and it converts to a privacy-friendly embed automatically. Watch links, share
        links, shorts, and bare video ids all work. To host a file yourself instead, upload an MP4. Clearing a slot
        restores the designed placeholder that says a film is in production.
      </p>

      {videoGroups.map((group) => (
        <section key={group}>
          <h2 className="font-serif text-2xl text-bright">{group}</h2>
          <div className="mt-5 space-y-5">
            {videoSlots
              .filter((s) => s.group === group)
              .map((slot) => {
                const live = (content.videos && content.videos[slot.key]) || {};
                const isLive = Boolean(live.embed || live.src);
                return (
                  <div key={slot.key} className="rounded-media border border-hairline bg-surface p-6">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h3 className="font-semibold text-bright">{slot.label}</h3>
                      <Pill tone={isLive ? 'good' : 'neutral'}>{isLive ? 'Published' : 'Placeholder showing'}</Pill>
                    </div>
                    {slot.help && <p className="mt-1 text-xs text-faint">{slot.help}</p>}

                    <div className="mt-4 grid gap-4 lg:grid-cols-2">
                      <div className="space-y-3">
                        <Field label="Video link" htmlFor={`url-${slot.key}`}>
                          <input
                            id={`url-${slot.key}`}
                            type="url"
                            placeholder="https://www.youtube.com/watch?v=..."
                            value={value(slot.key, 'url') || (live.embed ? live.embed : '')}
                            onChange={(e) => setDraft(slot.key, 'url', e.target.value)}
                            className={inputClass}
                          />
                        </Field>
                        <Field label="Caption (optional)" htmlFor={`cap-${slot.key}`}>
                          <input
                            id={`cap-${slot.key}`}
                            type="text"
                            value={value(slot.key, 'caption')}
                            onChange={(e) => setDraft(slot.key, 'caption', e.target.value)}
                            className={inputClass}
                          />
                        </Field>
                        <div className="flex flex-wrap items-center gap-3">
                          <button type="button" onClick={() => saveSlot(slot.key)} className="btn-primary !py-2 text-sm">
                            Save
                          </button>
                          <UploadButton
                            folder="videos"
                            accept="video/mp4,video/webm"
                            label="Upload MP4 instead"
                            maxMb={500}
                            onDone={(url) => {
                              setDraft(slot.key, 'src', url);
                              setDraft(slot.key, 'url', '');
                              toast('Uploaded. Choose Save to publish it.');
                            }}
                            onError={(m) => toast(m, 'bad')}
                          />
                          {isLive && (
                            <button
                              type="button"
                              onClick={() => clearSlot(slot.key)}
                              className="text-sm text-slate underline decoration-hairline underline-offset-4 hover:text-rose-300"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                        {value(slot.key, 'src') && !live.src && (
                          <p className="text-xs text-seal">File uploaded and ready. Choose Save to publish it.</p>
                        )}
                      </div>

                      <div>
                        <p className="mb-1.5 text-sm font-medium text-bright">Preview</p>
                        {live.embed ? (
                          <iframe
                            title={`${slot.label} preview`}
                            src={live.embed}
                            className="aspect-video w-full rounded border border-hairline"
                            allowFullScreen
                          />
                        ) : live.src ? (
                          <video src={live.src} controls className="aspect-video w-full rounded border border-hairline" />
                        ) : (
                          <div className="flex aspect-video items-center justify-center rounded border border-hairline bg-ink-deep/60 text-xs text-faint">
                            Nothing published yet
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </section>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------ image editor */

function ImageEditor({ content, save, toast }) {
  async function setImage(key, url) {
    await save({ images: { ...content.images, [key]: { src: url, updatedAt: Date.now() } } });
    toast('Saved. The site updates immediately.');
  }

  async function revert(key) {
    const next = { ...content.images };
    delete next[key];
    await save({ images: next });
    toast('Reverted to the image that ships with the site.');
  }

  return (
    <div className="space-y-12">
      <p className="max-w-measure-wide text-sm leading-relaxed text-slate">
        Upload a replacement for any image on the site. Reverting restores the file that ships with the build, so
        nothing is ever permanently lost. Screenshots look best as PNG; photographs as JPG.
      </p>

      {imageGroups.map((group) => (
        <section key={group}>
          <h2 className="font-serif text-2xl text-bright">{group}</h2>
          <div className="mt-5 grid gap-5 md:grid-cols-2">
            {imageSlots
              .filter((s) => s.group === group)
              .map((slot) => {
                const override = (content.images && content.images[slot.key]) || null;
                const current = (override && override.src) || slot.fallback;
                return (
                  <div key={slot.key} className="rounded-media border border-hairline bg-surface p-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <h3 className="text-sm font-semibold text-bright">{slot.label}</h3>
                      <Pill tone={override ? 'good' : 'neutral'}>{override ? 'Replaced' : 'Default'}</Pill>
                    </div>
                    {slot.help && <p className="mt-1 text-xs text-faint">{slot.help}</p>}
                    <div className="mt-3 overflow-hidden rounded border border-hairline bg-ink-deep/60">
                      {current ? (
                        <img src={current} alt="" className="h-40 w-full object-cover object-top" />
                      ) : (
                        <div className="flex h-40 items-center justify-center text-xs text-faint">No image set</div>
                      )}
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <UploadButton
                        folder="images"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        label={override ? 'Replace' : 'Upload'}
                        maxMb={25}
                        onDone={(url) => setImage(slot.key, url)}
                        onError={(m) => toast(m, 'bad')}
                      />
                      {override && (
                        <button
                          type="button"
                          onClick={() => revert(slot.key)}
                          className="text-sm text-slate underline decoration-hairline underline-offset-4 hover:text-bright"
                        >
                          Revert to default
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        </section>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------- blog editor */

const emptyPost = {
  slug: '',
  title: '',
  author: 'John C. Ferguson, MD, FACS',
  date: new Date().toISOString().slice(0, 10),
  category: 'Insights',
  description: '',
  readTime: '',
  featured: false,
  heroImage: '',
  content: '',
  status: 'draft',
};

function BlogEditor({ toast }) {
  const { posts, loading, staticPosts: seeded } = useAllPosts();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPost);
  const [slugTouched, setSlugTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  const update = (field) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => {
      const next = { ...f, [field]: v };
      if (field === 'title' && !slugTouched && !editing) next.slug = slugify(v);
      return next;
    });
  };

  function startNew() {
    setForm({ ...emptyPost, date: new Date().toISOString().slice(0, 10) });
    setSlugTouched(false);
    setEditing('new');
  }

  function startEdit(post) {
    setForm({ ...emptyPost, ...post });
    setSlugTouched(true);
    setEditing(post.slug);
  }

  function importSeeded(post) {
    setForm({ ...emptyPost, ...post, status: 'published' });
    setSlugTouched(true);
    setEditing(post.slug);
    toast('Loaded into the editor. Saving creates an editable copy that overrides the built-in version.');
  }

  async function savePost(status) {
    const slug = slugify(form.slug || form.title);
    if (!form.title.trim()) return toast('A title is needed.', 'bad');
    if (!slug) return toast('A web address (slug) is needed.', 'bad');
    if (!form.content.trim()) return toast('The post has no body text yet.', 'bad');

    setBusy(true);
    try {
      if (editing === 'new') {
        const existing = await getDoc(doc(db, 'blogPosts', slug));
        if (existing.exists()) {
          setBusy(false);
          return toast('A post already uses that web address. Change the slug.', 'bad');
        }
      }
      await setDoc(doc(db, 'blogPosts', slug), { ...form, slug, status, updatedAt: Date.now() }, { merge: true });
      toast(status === 'published' ? 'Published. It is live on the blog now.' : 'Saved as a draft. Not visible publicly.');
      setEditing(null);
    } catch {
      toast('Save failed. Confirm your account is in the admins collection.', 'bad');
    } finally {
      setBusy(false);
    }
  }

  async function removePost(post) {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'blogPosts', post.id));
      toast('Deleted.');
      if (editing === post.slug) setEditing(null);
    } catch {
      toast('Delete failed.', 'bad');
    }
  }

  if (editing) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setEditing(null)}
          className="text-sm text-slate underline decoration-hairline underline-offset-4 hover:text-bright"
        >
          Back to all posts
        </button>
        <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          <div className="space-y-4">
            <Field label="Title" htmlFor="p-title">
              <input id="p-title" type="text" value={form.title} onChange={update('title')} className={inputClass} />
            </Field>
            <Field label="Body" help="Markdown: ## for headings, ** ** for bold, - for bullets." htmlFor="p-content">
              <textarea
                id="p-content"
                rows="22"
                value={form.content}
                onChange={update('content')}
                className={`${inputClass} font-mono text-[13px] leading-relaxed`}
              />
            </Field>
          </div>
          <div className="space-y-4">
            <Field label="Web address" help="The post lives at /blog/this-value" htmlFor="p-slug">
              <input
                id="p-slug"
                type="text"
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setForm((f) => ({ ...f, slug: e.target.value }));
                }}
                className={inputClass}
              />
            </Field>
            <Field label="Summary" help="Shown on the blog index and in link previews." htmlFor="p-desc">
              <textarea id="p-desc" rows="4" value={form.description} onChange={update('description')} className={inputClass} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date" htmlFor="p-date">
                <input id="p-date" type="date" value={form.date} onChange={update('date')} className={inputClass} />
              </Field>
              <Field label="Reading time" htmlFor="p-read">
                <input id="p-read" type="text" placeholder="8 min read" value={form.readTime} onChange={update('readTime')} className={inputClass} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category" htmlFor="p-cat">
                <input id="p-cat" type="text" value={form.category} onChange={update('category')} className={inputClass} />
              </Field>
              <Field label="Author" htmlFor="p-author">
                <input id="p-author" type="text" value={form.author} onChange={update('author')} className={inputClass} />
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate">
              <input type="checkbox" checked={form.featured} onChange={update('featured')} className="h-4 w-4 accent-[#2dd4bf]" />
              Feature this post at the top of the blog
            </label>
            <Field label="Hero image (optional)">
              <div className="flex flex-wrap items-center gap-3">
                <UploadButton
                  folder="blog"
                  accept="image/png,image/jpeg,image/webp"
                  label={form.heroImage ? 'Replace image' : 'Upload image'}
                  maxMb={25}
                  onDone={(url) => setForm((f) => ({ ...f, heroImage: url }))}
                  onError={(m) => toast(m, 'bad')}
                />
                {form.heroImage && (
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, heroImage: '' }))}
                    className="text-sm text-slate underline decoration-hairline underline-offset-4 hover:text-bright"
                  >
                    Remove
                  </button>
                )}
              </div>
              {form.heroImage && <img src={form.heroImage} alt="" className="mt-3 h-32 w-full rounded border border-hairline object-cover" />}
            </Field>
            <div className="flex flex-wrap gap-3 border-t border-hairline pt-4">
              <button type="button" disabled={busy} onClick={() => savePost('published')} className="btn-primary !py-2 text-sm disabled:opacity-60">
                {form.status === 'published' ? 'Save and keep live' : 'Publish'}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => savePost('draft')}
                className="rounded border border-hairline px-3 py-2 text-sm font-semibold text-bright transition-colors hover:border-seal hover:text-seal disabled:opacity-60"
              >
                Save as draft
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const seededOnly = seeded.filter((s) => !posts.some((p) => p.slug === s.slug));

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="max-w-measure text-sm leading-relaxed text-slate">
          Posts written here appear on the blog immediately when published. Drafts stay private.
        </p>
        <button type="button" onClick={startNew} className="btn-primary !py-2 text-sm">
          Write a new post
        </button>
      </div>

      <div className="mt-8 divide-y divide-hairline border-y border-hairline">
        {loading && <p className="py-5 text-sm text-faint">Loading posts</p>}
        {!loading && posts.length === 0 && (
          <p className="py-5 text-sm text-faint">No posts written in the console yet.</p>
        )}
        {posts.map((post) => (
          <div key={post.id} className="flex flex-wrap items-center justify-between gap-4 py-4">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <p className="font-semibold text-bright">{post.title}</p>
                <Pill tone={post.status === 'published' ? 'good' : 'warn'}>
                  {post.status === 'published' ? 'Live' : 'Draft'}
                </Pill>
              </div>
              <p className="mt-1 text-xs text-faint">
                /blog/{post.slug} {post.date && `\u00b7 ${post.date}`}
              </p>
            </div>
            <div className="flex items-center gap-4">
              {post.status === 'published' && (
                <a
                  href={`/blog/${post.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-slate underline decoration-hairline underline-offset-4 hover:text-bright"
                >
                  View
                </a>
              )}
              <button type="button" onClick={() => startEdit(post)} className="text-sm font-semibold text-seal">
                Edit
              </button>
              <button
                type="button"
                onClick={() => removePost(post)}
                className="text-sm text-slate underline decoration-hairline underline-offset-4 hover:text-rose-300"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {seededOnly.length > 0 && (
        <div className="mt-12">
          <h2 className="font-serif text-2xl text-bright">Posts built into the site</h2>
          <p className="mt-2 max-w-measure-wide text-sm leading-relaxed text-slate">
            These ship with the build and are always live. Loading one into the editor creates an editable copy that
            overrides the built-in version, leaving the original in the code untouched.
          </p>
          <div className="mt-5 divide-y divide-hairline border-y border-hairline">
            {seededOnly.map((post) => (
              <div key={post.slug} className="flex flex-wrap items-center justify-between gap-4 py-4">
                <div>
                  <p className="font-semibold text-bright">{post.title}</p>
                  <p className="mt-1 text-xs text-faint">/blog/{post.slug}</p>
                </div>
                <button type="button" onClick={() => importSeeded(post)} className="text-sm font-semibold text-seal">
                  Load into editor
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ shell */

const TABS = [
  { id: 'videos', label: 'Videos' },
  { id: 'images', label: 'Images' },
  { id: 'blog', label: 'Writing' },
];

function Console() {
  const { user, isAdmin, loading } = useAdminAuth();
  const [tab, setTab] = useState('videos');
  const [content, setContent] = useState({ videos: {}, images: {} });
  const [toastState, setToastState] = useState({ message: '', tone: 'good' });

  const toast = useMemo(
    () => (message, tone = 'good') => {
      setToastState({ message, tone });
      setTimeout(() => setToastState({ message: '', tone: 'good' }), 4000);
    },
    []
  );

  useEffect(() => {
    if (!isAdmin) return;
    getDoc(doc(db, ...CONTENT_DOC))
      .then((snap) => {
        const d = snap.exists() ? snap.data() : {};
        setContent({ videos: d.videos || {}, images: d.images || {} });
      })
      .catch(() => setContent({ videos: {}, images: {} }));
  }, [isAdmin]);

  async function save(patch) {
    const next = { ...content, ...patch };
    setContent(next);
    await setDoc(doc(db, ...CONTENT_DOC), { ...next, updatedAt: Date.now() }, { merge: true });
  }

  if (loading) {
    return <div className="px-6 py-24 text-center text-sm text-faint">Checking your sign-in</div>;
  }
  if (!user) return <SignIn />;
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-md px-6 py-24 text-center">
        <h1 className="font-serif text-2xl text-bright">This account is not an administrator</h1>
        <p className="mt-3 text-sm leading-relaxed text-slate">
          You are signed in as {user.email}, but there is no matching record in the admins collection. Add a document
          with this account’s user id to grant access.
        </p>
        <button type="button" onClick={signOut} className="btn-secondary mt-6 !py-2 text-sm">
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink">
      <header className="border-b border-hairline">
        <div className="mx-auto flex w-full max-w-site flex-wrap items-center justify-between gap-4 px-6 py-4 md:px-10">
          <div className="flex items-center gap-3">
            <img src="/logo/edai-mark-dark-transparent.svg" alt="" aria-hidden="true" className="h-6 w-auto" />
            <span className="font-serif text-lg text-bright">Content console</span>
          </div>
          <div className="flex items-center gap-5 text-sm">
            <Link to="/" className="text-slate hover:text-bright">
              View site
            </Link>
            <span className="hidden text-faint sm:inline">{user.email}</span>
            <button type="button" onClick={signOut} className="font-semibold text-seal">
              Sign out
            </button>
          </div>
        </div>
        <nav className="mx-auto flex w-full max-w-site gap-1 px-6 md:px-10" aria-label="Console sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              aria-current={tab === t.id ? 'page' : undefined}
              className={`-mb-px border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${
                tab === t.id ? 'border-seal text-bright' : 'border-transparent text-slate hover:text-bright'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="mx-auto w-full max-w-site px-6 py-10 md:px-10">
        {tab === 'videos' && <VideoEditor content={content} save={save} toast={toast} />}
        {tab === 'images' && <ImageEditor content={content} save={save} toast={toast} />}
        {tab === 'blog' && <BlogEditor toast={toast} />}
      </main>

      <Toast message={toastState.message} tone={toastState.tone} />
    </div>
  );
}

export default function AdminPage() {
  return (
    <AdminAuthProvider>
      <Helmet>
        <title>Content console | EdAI Systems</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Console />
    </AdminAuthProvider>
  );
}
